import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import './RoleForm.css';

const channelPermissionGroups = [
  { label: 'SRR', names: ['/itemWebChannelListSRR', '/itemWebChannelSRR/:itemId/edit', '/srr-integration'] },
  { label: 'Liftoutlet', names: ['/itemWebChannelListLO', '/itemWebChannelLO/:itemId/edit', '/lo-integration'] },
  { label: 'HP', names: ['/itemWebChannelListHP', '/itemWebChannelHP/:itemId/edit', '/hp-integration'] },
];

const RoleForm = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Editing states
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedPermissions, setEditedPermissions] = useState([]);

  // Fetch roles and permissions
  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Rollen:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await api.get('/permissions');
      setPermissions(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Permissions:', error);
    }
  };

  useEffect(() => {
    Promise.all([fetchRoles(), fetchPermissions()]).finally(() => setLoading(false));
  }, []);

  // Add new role
  const handleAddRole = async () => {
    if (!newName.trim()) {
      alert('Bitte einen Namen für die Rolle eingeben!');
      return;
    }
    try {
      await api.post('/roles', { name: newName, description: newDescription });
      setNewName('');
      setNewDescription('');
      fetchRoles();
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Rolle:', error);
      alert('Fehler beim Hinzufügen der Rolle');
    }
  };

  // Delete role
  const handleDeleteRole = async (id) => {
    if (!window.confirm('Rolle wirklich löschen?')) return;
    try {
      await api.delete(`/roles/${id}`);
      setRoles(roles.filter(r => r.id !== id));
    } catch (error) {
      console.error('Fehler beim Löschen der Rolle:', error);
      alert('Fehler beim Löschen der Rolle');
    }
  };

  // Start editing
  const handleEditRole = async (role) => {
    setEditingId(role.id);
    setEditedName(role.name);
    setEditedDescription(role.description || '');
    try {
      const res = await api.get(`/role-permissions/role/${role.id}`);
      setEditedPermissions(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Role-Permissions:', error);
      setEditedPermissions([]);
    }
  };

  // Toggle a permission on/off
  const handleTogglePermission = (permId) => {
    setEditedPermissions(prev =>
      prev.includes(permId)
        ? prev.filter(id => id !== permId)
        : [...prev, permId]
    );
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedName('');
    setEditedDescription('');
    setEditedPermissions([]);
  };

  // Update role and its permissions
  const handleUpdateRole = async (id) => {
    if (!editedName.trim()) {
      alert('Name darf nicht leer sein!');
      return;
    }
    try {
      await api.put(`/roles/${id}`, { name: editedName, description: editedDescription });
      await api.put(`/role-permissions/role/${id}`, { permissionIds: editedPermissions });
      handleCancelEdit();
      fetchRoles();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Rolle:', error);
      alert('Fehler beim Aktualisieren der Rolle');
    }
  };

  const groupedPermissions = channelPermissionGroups.map(group => ({
    ...group, permissions: group.names.map(name => permissions.find(p => p.name === name)).filter(Boolean),
  })).filter(group => group.permissions.length > 0);
  const otherPermissions = permissions.filter(permission =>
    !channelPermissionGroups.some(group => group.names.includes(permission.name))
  );

  const renderPermission = (permission) => (
    <label key={permission.id} className="role-management-permission">
      <input
        type="checkbox"
        checked={editedPermissions.includes(permission.id)}
        onChange={() => handleTogglePermission(permission.id)}
      />
      <span>{permission.description || permission.name}</span>
    </label>
  );

  if (loading) {
    return <div>Daten werden geladen...</div>;
  }

  return (
    <div className="role-management">
      <div className="role-management-content">
        <Helmet>
          <title>Rollen verwalten</title>
          <meta name="description" content="Rollen verwalten" />
        </Helmet>

        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Rollen verwalten</h2>

        {/* Add form */}
        <div className="role-management-add">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Rollenname"
            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <input
            type="text"
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            placeholder="Beschreibung"
            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <button
            onClick={handleAddRole}
            style={{
              padding: '6px 12px',
              background: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Hinzufügen
          </button>
        </div>

        {/* Roles list */}
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {roles.map(role => (
            <li key={role.id} className="role-management-row">
              {editingId === role.id ? (
                <div className="role-management-editor">
                  <div className="role-management-fields">
                    <label>
                      Rollenname
                      <input
                        type="text"
                        value={editedName}
                        onChange={e => setEditedName(e.target.value)}
                        style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </label>
                    <label>
                      Beschreibung
                      <input
                        type="text"
                        value={editedDescription}
                        onChange={e => setEditedDescription(e.target.value)}
                        style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </label>
                  </div>
                  <fieldset className="role-management-permissions">
                    <legend>Berechtigungen</legend>
                    <div className="role-management-permission-grid">
                      {otherPermissions.map(renderPermission)}
                      {groupedPermissions.map(group => (
                        <div key={group.label} className="role-management-permission-group" role="group" aria-label={group.label}>
                          {group.permissions.map(renderPermission)}
                        </div>
                      ))}
                    </div>
                  </fieldset>
                  <div className="role-management-actions">
                    <button onClick={() => handleUpdateRole(role.id)}>Speichern</button>
                    <button onClick={handleCancelEdit}>Abbrechen</button>
                  </div>
                </div>
              ) : (
                <>
                  <span>{role.name}</span>
                  <span>{role.description}</span>
                  <div className="role-management-actions">
                    <button onClick={() => handleEditRole(role)} style={{
                      fontSize: '15px',
                      padding: '5px 10px',
                      background: '#007bff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                    }}>
                      Bearbeiten
                    </button>
                    <button onClick={() => handleDeleteRole(role.id)} style={{
                      fontSize: '15px',
                      padding: '5px 10px',
                      background: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}>
                      Löschen
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default RoleForm;
